/**
 * Каталог задач и датасеты (docs/06-api.md, раздел «Каталог»).
 *
 * Каталог открыт гостю, но авторизованному он показывает прогресс, поэтому
 * идентификатор пользователя входит в ключ запроса: после восстановления
 * сессии по refresh-cookie данные должны перезапроситься сами, иначе вошедший
 * увидел бы гостевую версию каталога до перезагрузки страницы.
 */

import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { useCurrentUser } from './auth'
import { apiFetch } from './client'
import { authFetch, getAccessToken } from './session'

export const topicSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  position: z.number(),
  tasks_total: z.number(),
  tasks_solved: z.number().nullable().default(null),
})

export const topicListSchema = z.object({ items: z.array(topicSchema) })

// Поля `solution_revealed` и `needs_review` обязательные: значение
// по умолчанию скрыло бы расхождение со схемой ответа, а именно так каталог
// и показывал «решено» вместо «решено с подсказками» тому, кто открыл решение.
export const taskProgressSchema = z.object({
  status: z.enum(['attempted', 'solved']),
  attempts_count: z.number(),
  hints_revealed: z.number(),
  solution_revealed: z.boolean(),
  // Задача решена по подсказкам, и срок на самостоятельное решение наступил
  // (FR-P8). Считает сервер: условие зависит от текущего момента, а часы
  // у браузера свои.
  needs_review: z.boolean(),
})

export const topicRefSchema = z.object({ slug: z.string(), title: z.string() })

export const taskListItemSchema = z.object({
  slug: z.string(),
  title: z.string(),
  topic: topicRefSchema,
  difficulty: z.enum(['easy', 'medium', 'hard']),
  tags: z.array(z.string()),
  progress: taskProgressSchema.nullable().default(null),
})

export const taskListSchema = z.object({
  items: z.array(taskListItemSchema),
  total: z.number(),
  page: z.number(),
  per_page: z.number(),
})

export const hintSchema = z.object({
  position: z.number(),
  title: z.string(),
  revealed: z.boolean(),
  body_md: z.string().nullable().default(null),
})

export const taskDetailSchema = z.object({
  slug: z.string(),
  title: z.string(),
  statement_md: z.string(),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  task_type: z.string(),
  // Только у задачи на изменение данных (docs/adr/0006-dml-tasks.md).
  dml_mode: z.enum(['rollback', 'committed']).nullable().default(null),
  // Что будет проверено после запроса. Текста проверочных запросов здесь нет:
  // он назвал бы часть решения (docs/06-api.md).
  checks: z
    .array(
      z.object({
        name: z.string(),
        title: z.string(),
        kind: z.enum(['effect', 'no_side_effect']),
      }),
    )
    .default([]),
  status: z.enum(['draft', 'published', 'archived']),
  topic: topicRefSchema,
  tags: z.array(z.string()),
  dataset: z.object({ slug: z.string(), title: z.string() }),
  hints: z.array(hintSchema),
  expected_columns: z.array(z.object({ name: z.string() })),
  grading: z.object({ order_matters: z.boolean(), check_column_names: z.boolean() }),
  progress: taskProgressSchema.nullable().default(null),
  explanation_available: z.boolean().default(false),
})

export const datasetSchemaSchema = z.object({
  slug: z.string(),
  tables: z.array(
    z.object({
      name: z.string(),
      columns: z.array(
        z.object({
          name: z.string(),
          type: z.string(),
          nullable: z.boolean(),
          primary_key: z.boolean().default(false),
        }),
      ),
      foreign_keys: z.array(z.object({ columns: z.array(z.string()), references: z.string() })),
      row_count: z.number(),
    }),
  ),
})

export const previewSchema = z.object({
  columns: z.array(z.object({ name: z.string(), type: z.string() })),
  rows: z.array(z.array(z.string().nullable())),
  truncated: z.boolean().default(false),
})

export type Topic = z.infer<typeof topicSchema>
export type TaskListItem = z.infer<typeof taskListItemSchema>
export type TaskList = z.infer<typeof taskListSchema>
export type TaskDetail = z.infer<typeof taskDetailSchema>
export type TaskProgress = z.infer<typeof taskProgressSchema>
export type Hint = z.infer<typeof hintSchema>
export type DatasetSchema = z.infer<typeof datasetSchemaSchema>
export type DatasetTable = DatasetSchema['tables'][number]
export type Preview = z.infer<typeof previewSchema>
export type Difficulty = TaskListItem['difficulty']

// Единственный список значений фильтра статуса: по нему каталог рисует пункты,
// он же типизирует запрос. Копии разъезжались бы молча — пункт в интерфейсе
// есть, а сервер отвечает 422.
export const TASK_STATUS_FILTERS = ['solved', 'attempted', 'unsolved', 'review'] as const

export type TaskStatusFilter = (typeof TASK_STATUS_FILTERS)[number]

export interface TaskFilters {
  topic?: string | undefined
  difficulty?: Difficulty | undefined
  status?: TaskStatusFilter | undefined
  tag?: string | undefined
  page?: number | undefined
}

export const catalogKeys = {
  topics: (viewer: string | null) => ['catalog', 'topics', viewer] as const,
  tasks: (viewer: string | null, filters: TaskFilters) =>
    ['catalog', 'tasks', viewer, filters] as const,
  task: (viewer: string | null, slug: string) => ['catalog', 'task', viewer, slug] as const,
  datasetSchema: (slug: string) => ['dataset', 'schema', slug] as const,
  preview: (slug: string, table: string, limit: number) =>
    ['dataset', 'preview', slug, table, limit] as const,
}

/**
 * Запрос, который работает и гостю, и вошедшему.
 *
 * `authFetch` без токена сначала пробует обновить сессию, а для гостя это
 * заведомо неудачный запрос к `/auth/refresh` — поэтому анонимный случай
 * идёт обычным `apiFetch`.
 */
function fetchPublic<T>(path: string): Promise<T> {
  return getAccessToken() ? authFetch<T>(path) : apiFetch<T>(path)
}

function queryString(filters: TaskFilters, perPage?: number): string {
  const params = new URLSearchParams()
  if (filters.topic) params.set('topic', filters.topic)
  if (filters.difficulty) params.set('difficulty', filters.difficulty)
  if (filters.status) params.set('status', filters.status)
  if (filters.tag) params.set('tag', filters.tag)
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (perPage) params.set('per_page', String(perPage))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export async function fetchTopics(): Promise<Topic[]> {
  return topicListSchema.parse(await fetchPublic<unknown>('/topics')).items
}

export async function fetchTasks(filters: TaskFilters, perPage: number): Promise<TaskList> {
  return taskListSchema.parse(await fetchPublic<unknown>(`/tasks${queryString(filters, perPage)}`))
}

export async function fetchTask(slug: string): Promise<TaskDetail> {
  return taskDetailSchema.parse(await fetchPublic<unknown>(`/tasks/${encodeURIComponent(slug)}`))
}

export async function fetchDatasetSchema(slug: string): Promise<DatasetSchema> {
  return datasetSchemaSchema.parse(
    await fetchPublic<unknown>(`/datasets/${encodeURIComponent(slug)}/schema`),
  )
}

export async function fetchPreview(slug: string, table: string, limit: number): Promise<Preview> {
  const query = new URLSearchParams({ table, limit: String(limit) })
  return previewSchema.parse(
    await fetchPublic<unknown>(`/datasets/${encodeURIComponent(slug)}/preview?${query.toString()}`),
  )
}

/** Идентификатор смотрящего для ключей запросов: гость — `null`. */
export function useViewerId(): string | null {
  const { data } = useCurrentUser()
  return data?.id ?? null
}

export function useTopics() {
  const viewer = useViewerId()
  return useQuery({ queryKey: catalogKeys.topics(viewer), queryFn: fetchTopics })
}

export const TASKS_PER_PAGE = 50

export function useTasks(filters: TaskFilters) {
  const viewer = useViewerId()
  return useQuery({
    queryKey: catalogKeys.tasks(viewer, filters),
    queryFn: () => fetchTasks(filters, TASKS_PER_PAGE),
  })
}

export function useTask(slug: string) {
  const viewer = useViewerId()
  return useQuery({
    queryKey: catalogKeys.task(viewer, slug),
    queryFn: () => fetchTask(slug),
    retry: false,
  })
}

export function useDatasetSchema(slug: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.datasetSchema(slug ?? ''),
    queryFn: () => fetchDatasetSchema(slug ?? ''),
    enabled: Boolean(slug),
    // Схема датасета меняется только при пересборке песочницы.
    staleTime: 60 * 60 * 1000,
  })
}

export const PREVIEW_ROWS = 10

export function usePreview(slug: string | undefined, table: string | null) {
  return useQuery({
    queryKey: catalogKeys.preview(slug ?? '', table ?? '', PREVIEW_ROWS),
    queryFn: () => fetchPreview(slug ?? '', table ?? '', PREVIEW_ROWS),
    enabled: Boolean(slug) && Boolean(table),
    staleTime: 60 * 60 * 1000,
  })
}
